import { ChevronRight, Headphones } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AppDialog, FixedButton, MobileShell, TopNav } from "./components";
import { useCurrentUser, useInquiries, useInquiry } from "./api";
import { navigate } from "./router";
import { requestApi, setApiToken } from "../shared/api";
import { formatPhone } from "./utils";

export function MyScreen() {
  const [dialog, setDialog] = useState<"logout" | "withdraw" | null>(null);
  const { data: user } = useCurrentUser();
  const menuItems = [
    { label: "내 정보 수정", action: () => navigate("profile"), arrow: true },
    { label: "결제 내역", action: () => navigate("paymentHistory"), arrow: true },
    { label: "견적 요청 내역", action: () => navigate("quotes", { source: "my" }), arrow: true },
    { label: "1:1 문의", action: () => navigate("inquiries"), arrow: true },
    { label: "로그아웃", action: () => setDialog("logout"), arrow: false },
    { label: "회원탈퇴", action: () => setDialog("withdraw"), arrow: false },
  ];
  return (
    <MobileShell bottomNav className="my-screen">
      <header className="my-header"><h1>MY</h1></header>
      <section className="profile-card">
        <img src={user?.avatarUrl || "/assets/profile-avatar.png"} alt="프로필" />
        <div><strong>{user?.name || "회원"} 님</strong><small>@{user?.loginId || "-"}</small></div>
      </section>
      <section className="my-menu">
        {menuItems.map((item) => <button type="button" key={item.label} onClick={item.action}><span>{item.label}</span>{item.arrow && <ChevronRight size={20} />}</button>)}
      </section>
      {dialog === "logout" && <AppDialog
        title="로그아웃하시겠습니까?" description="확인을 누르면 로그인 화면으로 이동합니다."
        confirmText="로그아웃" cancelText="취소" onCancel={() => setDialog(null)}
        onConfirm={async () => {
          try { await requestApi("/auth/logout", { method: "POST" }); } catch { /* local logout still proceeds */ }
          setApiToken("");
          navigate("login");
        }}
      />}
      {dialog === "withdraw" && <AppDialog
        title="회원탈퇴를 진행하시겠습니까?" description="탈퇴 후에는 계정과 이용 정보를 복구할 수 없습니다."
        confirmText="회원탈퇴" cancelText="취소" onCancel={() => setDialog(null)} onConfirm={() => navigate("login")}
      />}
    </MobileShell>
  );
}

export function ProfileScreen() {
  const [saved, setSaved] = useState(false);
  const [changed, setChanged] = useState(false);
  const { data: user, reload } = useCurrentUser();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [gender, setGender] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [avatarFileId, setAvatarFileId] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setPhone(formatPhone(user.phone || ""));
    setBirthDate(user.birthDate || "");
    setGender(user.gender || "unspecified");
    setAvatarFileId(user.avatarFileId || "");
    setAvatarPreview(user.avatarUrl || "");
  }, [user]);
  const invalidPassword = Boolean(password && (password.length < 8 || password !== passwordConfirm));
  const isKakaoOnly = Boolean(user?.authProviders?.includes("kakao") && !user.authProviders.includes("password"));
  return (
    <MobileShell className="profile-edit-screen has-fixed-action">
      <TopNav title="내 정보 수정" fallback="my" />
      <section className="profile-edit-content">
        <div className="avatar-edit">
          <img src={avatarPreview || "/assets/profile-avatar.png"} alt="프로필" />
          <button type="button" onClick={() => avatarInputRef.current?.click()}>프로필 변경</button>
          <input
            ref={avatarInputRef}
            className="sr-only"
            type="file"
            accept="image/jpeg,image/png"
            onChange={async (event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              if (file.size > 3 * 1024 * 1024) { setError("프로필 이미지는 3MB 이하만 등록할 수 있습니다."); return; }
              setAvatarPreview(URL.createObjectURL(file));
              const form = new FormData();
              form.append("file", file);
              form.append("ownerType", "avatar");
              try {
                const uploaded = await requestApi<{ id: string; url: string }>("/files", { method: "POST", body: form });
                setAvatarFileId(uploaded.id);
                setAvatarPreview(uploaded.url);
                setChanged(true);
              } catch (reason) { setError(reason instanceof Error ? reason.message : "프로필 이미지를 저장하지 못했습니다."); }
            }}
          />
        </div>
        <h2>정보 입력</h2>
        <div className="field-stack">
          <label>이름</label>
          <input value={name} onChange={(event) => { setName(event.target.value); setChanged(true); }} />
          <label>아이디</label>
          <input value={user?.loginId || ""} disabled />
          <p className="field-help">*아이디는 수정이 불가능합니다.</p>
          {!isKakaoOnly && <>
            <label>비밀번호</label>
            <input type="password" value={password} placeholder="변경할 비밀번호를 입력해 주세요." onChange={(event) => { setPassword(event.target.value); setChanged(true); }} />
            <input type="password" value={passwordConfirm} placeholder="비밀번호를 다시 입력해 주세요." onChange={(event) => { setPasswordConfirm(event.target.value); setChanged(true); }} />
          </>}
          <label>휴대전화 번호</label>
          <input value={phone} inputMode="tel" onChange={(event) => { setPhone(formatPhone(event.target.value)); setChanged(true); }} />
          <label>이메일</label>
          <input value={user?.email || ""} disabled />
          <p className="field-help">*이메일 수정은 고객센터에 문의해 주시기 바랍니다.</p>
          <label>생년월일</label>
          <input type="date" max={new Date().toISOString().slice(0, 10)} value={birthDate} onChange={(event) => { setBirthDate(event.target.value); setChanged(true); }} />
          <fieldset className="profile-gender-field">
            <legend>성별</legend>
            <div>
              <button type="button" className={gender === "male" ? "is-active" : ""} onClick={() => { setGender("male"); setChanged(true); }}>남성</button>
              <button type="button" className={gender === "female" ? "is-active" : ""} onClick={() => { setGender("female"); setChanged(true); }}>여성</button>
            </div>
          </fieldset>
          {invalidPassword && <p className="form-error">비밀번호는 8자 이상이며 확인값과 일치해야 합니다.</p>}
          {error && <p className="form-error">{error}</p>}
        </div>
      </section>
      <FixedButton disabled={!changed || !name.trim() || invalidPassword} onClick={async () => {
        setError("");
        try {
          await requestApi("/auth/profile", { method: "PUT", body: JSON.stringify({ name: name.trim(), phone, birthDate: birthDate || undefined, gender, avatarFileId: avatarFileId || undefined, ...(!isKakaoOnly && password ? { password } : {}) }) });
          setSaved(true); setChanged(false); setPassword(""); setPasswordConfirm(""); reload();
        } catch (reason) { setError(reason instanceof Error ? reason.message : "회원정보 저장에 실패했습니다."); }
      }}>완료</FixedButton>
      {saved && <AppDialog title="회원정보 수정이 완료되었습니다." description="변경한 회원정보가 저장되었습니다." onConfirm={() => { setSaved(false); navigate("my"); }} />}
    </MobileShell>
  );
}

export function InquiriesScreen() {
  const [filter, setFilter] = useState("전체");
  const filters = ["전체", "서비스 이용 문의", "탈퇴 문의", "환불 문의"];
  const { data: inquiries, loading, error } = useInquiries();
  const visible = filter === "전체" ? inquiries : inquiries.filter((item) => item.inquiryType === filter);
  return (
    <MobileShell className="inquiry-list-screen">
      <TopNav title="1:1 문의" fallback="my" />
      <div className="filter-tabs filter-tabs--scroll">{filters.map((item) => <button type="button" key={item} className={filter === item ? "is-active" : ""} onClick={() => setFilter(item)}>{item}</button>)}</div>
      <section className="inquiry-help"><Headphones size={24} strokeWidth={1.7} aria-hidden="true" /><strong>도움이 필요하신가요?</strong><button type="button" onClick={() => navigate("inquiryWrite")}>문의하기</button></section>
      <div className="inquiry-list-data">
        {loading && <p className="inquiry-empty">문의 내역을 불러오는 중입니다.</p>}
        {!loading && error && <p className="inquiry-empty">{error}</p>}
        {!loading && !error && !visible.length && <p className="inquiry-empty">진행중인 문의가 없습니다.</p>}
        {visible.map((item) => <button className="plain-list-row" type="button" key={item.id} onClick={() => navigate("inquiryDetail", { id: item.id })}>
          <strong>[{item.inquiryType}] {item.title}</strong><span>{item.status === "answered" ? "답변완료" : "답변대기"} · {item.createdAt.slice(0, 10)}</span>
        </button>)}
      </div>
    </MobileShell>
  );
}

export function InquiryWriteScreen() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState("");
  const [error, setError] = useState("");
  return (
    <MobileShell className="form-screen has-fixed-action">
      <TopNav title="문의하기" fallback="inquiries" />
      <section className="inquiry-write-content"><div className="field-stack">
        <label>문의 구분</label>
        <select value={type} onChange={(event) => setType(event.target.value)}><option value="" disabled>문의 유형을 선택해 주세요.</option><option>서비스 이용 문의</option><option>탈퇴 문의</option><option>환불 문의</option><option>기타 문의</option></select>
        <label>문의 제목</label><input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="문의 제목을 입력해 주세요." />
        <label>문의 내용</label><textarea value={body} onChange={(event) => setBody(event.target.value)} placeholder="문의 내용을 입력해 주세요." />
        {error && <p className="form-error">{error}</p>}
      </div></section>
      <FixedButton disabled={!type || !title || !body} onClick={async () => {
        setError("");
        try { await requestApi("/inquiries", { method: "POST", body: JSON.stringify({ inquiryType: type, title, body }) }); navigate("inquiries"); }
        catch (reason) { setError(reason instanceof Error ? reason.message : "문의 등록에 실패했습니다."); }
      }}>완료</FixedButton>
    </MobileShell>
  );
}

export function InquiryDetailScreen() {
  const id = new URL(location.href).searchParams.get("id");
  const { data: inquiry, loading, error } = useInquiry(id);
  if (loading) return <MobileShell><TopNav title="문의 상세" fallback="inquiries" /><p className="inquiry-empty">문의를 불러오는 중입니다.</p></MobileShell>;
  if (!inquiry || error) return <MobileShell><TopNav title="문의 상세" fallback="inquiries" /><p className="inquiry-empty">{error || "문의를 찾을 수 없습니다."}</p></MobileShell>;
  return (
    <MobileShell><TopNav title="문의 상세" fallback="inquiries" /><article className="inquiry-detail-content">
      <span>[{inquiry.inquiryType}]</span><h1>{inquiry.title}</h1><p>{inquiry.body}</p><time>{inquiry.createdAt.slice(0, 10)}</time>
      {inquiry.answerBody && <section><small>[답변 완료]</small><h2>‘{inquiry.title}’의 답변입니다.</h2><p>{inquiry.answerBody}</p><time>{String(inquiry.answeredAt || "").slice(0, 10)}</time></section>}
    </article></MobileShell>
  );
}
